// Handing out org keys: the half of "granting access" that cannot happen on the server.
//
// A grant authorizes; it hands out no data. Reading a vault additionally needs its org_key wrapped
// onto the user's public key (`org_members`), and only a browser that already holds that key can
// produce the wrap (§7) — the server never sees it, and it cannot be wrapped to a *group* at all
// (HPKE targets one public key). So assigning a role to a group used to be a silent no-op: the
// members were authorized, but the vault list comes from `org_members`, so they saw nothing.
//
// The backend reports every such pair at GET /admin/access/gaps; the functions here drain that
// list from the admin's browser. Decision logic lives in the pure `access-plan.ts`.

import { useEffect, useState } from "react";
import { api, type AccessGap, type Organization } from "../api/client";
import { b64decode, b64encode, wrapOrgKeyTo } from "../crypto/vault";
import { planDistribution, summarize, type DistRow } from "./access-plan";

/** Wrap each ready row's org key to its user and register them as a key holder. */
export async function distributeKeys(
  rows: DistRow[],
  orgs: Organization[],
  orgKeyFor: (org: Organization) => Uint8Array,
  log: (line: string) => void,
): Promise<{ ok: number; failed: number }> {
  const byId = new Map(orgs.map((o) => [o.id, o]));
  let ok = 0;
  let failed = 0;
  for (const row of rows) {
    if (row.status !== "ready") continue;
    const org = byId.get(row.orgId);
    if (!org) {
      failed++;
      log(`✗ ${row.orgName} → ${row.login}: you are not a key holder of this vault`);
      continue;
    }
    try {
      // One row failing must not abandon the rest — each pair is independent.
      const wrapped = wrapOrgKeyTo(b64decode(row.publicKey), orgKeyFor(org));
      await api.addMember(row.orgId, row.userId, b64encode(wrapped));
      ok++;
      log(`✓ ${row.orgName} → ${row.login}`);
    } catch (e) {
      failed++;
      log(`✗ ${row.orgName} → ${row.login}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return { ok, failed };
}

export interface GrantRequest {
  principal_type: string;
  principal_id: string;
  scope: string;
  node_id?: string;
  role?: string;
  capabilities?: string[];
}

/**
 * Create a grant and hand out the keys it implies, as ONE action.
 *
 * `userIds` are the people the grant affects (a user, or a group's members). Whatever cannot be
 * done now — the user has never enrolled, or this admin does not hold that vault's key — is
 * reported rather than swallowed, and stays listed in the standing key-distribution view.
 */
export async function grantAndDistribute(args: {
  grant: GrantRequest;
  userIds: string[];
  orgKeyFor: (org: Organization) => Uint8Array;
  log: (line: string) => void;
}): Promise<{ ok: number; failed: number; blocked: number }> {
  await api.createGrant(args.grant);
  args.log("✓ Grant created.");
  const [gapsRes, orgsRes] = await Promise.all([api.getAccessGaps(), api.listOrgs()]);
  const orgs = orgsRes.organizations.filter((o) => !o.is_personal);
  const rows = planDistribution({
    gaps: gapsRes.gaps,
    adminOrgIds: orgs.map((o) => o.id),
    userIds: args.userIds,
  });
  const s = summarize(rows);
  if (s.total === 0) {
    args.log("No keys to distribute — everyone affected already holds them.");
    return { ok: 0, failed: 0, blocked: 0 };
  }
  const res = await distributeKeys(rows, orgs, args.orgKeyFor, args.log);
  if (s.byStatus["not-enrolled"] > 0) {
    args.log(`⚠ ${s.byStatus["not-enrolled"]} pending: user not enrolled yet (no public key).`);
  }
  if (s.byStatus["no-admin-key"] > 0) {
    args.log(`⚠ ${s.byStatus["no-admin-key"]} pending: you do not hold that vault's key.`);
  }
  return { ...res, blocked: s.blocked };
}

/**
 * Load the gaps plus the vaults this admin holds, and plan the work.
 * `userIds` narrows the plan to the people a just-created grant affected.
 */
export function useDistributionPlan(userIds?: string[]) {
  const [gaps, setGaps] = useState<AccessGap[] | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [n, setN] = useState(0);

  useEffect(() => {
    let live = true;
    Promise.all([api.getAccessGaps(), api.listOrgs()])
      .then(([g, o]) => {
        if (!live) return;
        setGaps(g.gaps);
        setOrgs(o.organizations.filter((x) => !x.is_personal));
        setErr(null);
      })
      .catch((e) => live && setErr(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [n]);

  const rows = gaps ? planDistribution({ gaps, adminOrgIds: orgs.map((o) => o.id), userIds }) : null;
  return { rows, orgs, err, loading: gaps === null, reload: () => setN((x) => x + 1) };
}
